import { Component, Inject, OnInit } from "@angular/core";
import { IReflection, ReflectionQuestion, ReflectionQuestionType } from "@webapp/reflections/models/reflections.models";
import { ModalInstanceClass, ModalInstanceToken } from "@webapp/shared";
import { FormArray, FormBuilder, Validators } from "@angular/forms";
import { ReflectionsFacade } from "@webapp/reflections/services/reflections-facade.service";
import { Ng1StateProxy } from "@gtmhub/core/routing";
import { take, takeUntil } from "rxjs";
import { generateCustomReflectionQuestion } from "./helpers";
import { IReflectionQuestionControlsConfig, UpdateReflectionQuestionType } from "@webapp/reflections/models/manage-reflections.models";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { TeamsFacade } from "@webapp/teams/services/teams-facade.service";
import { Team } from "@webapp/teams/models/teams.models";
@Component({
  selector: "manage-reflections",
  templateUrl: "./manage-reflections.component.html",
  styleUrls: ["./manage-reflections.component.less", "./manage-question/manage-question.component.less"],
})
export class ManageReflectionsComponent implements OnInit {
  public formGroup = this.formBuilder.group({
    questions: this.formBuilder.array([]),
    newQuestionTitle: ["", [Validators.required, Validators.maxLength(200), Validators.minLength(1)]],
  });
  public showQuestionInput = false;
  public questions: ReflectionQuestion<ReflectionQuestionType>[] = [];
  public teamReflection: IReflection;
  public isTeamReflectionEnabled: boolean;
  public team: Team;

  constructor(
    @Inject(ModalInstanceToken) private modalInstance: ModalInstanceClass,
    private formBuilder: FormBuilder,
    private reflectionsFacade: ReflectionsFacade,
    private ng1StateProxy: Ng1StateProxy,
    private destroy$: DestroyService,
    private teamsFacade: TeamsFacade
  ) {}

  public ngOnInit(): void {
    const teamId = this.ng1StateProxy.getParam("teamId");
    this.reflectionsFacade
      .getTeamReflection$({ teamId })
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.teamReflection = data;
        this.isTeamReflectionEnabled = this.teamReflection.isEnabled;
        this.setQuestions();
        this.buildForm();
      });
    this.teamsFacade
      .getTeam$(teamId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((team: Team) => {
        this.team = team;
      });
  }

  private setQuestions(): void {
    this.questions = this.teamReflection.questions;
  }

  private buildForm(): void {
    this.questions.forEach((question) => {
      this.addFormQuestion(question);
    });
  }

  private addFormQuestion(question: ReflectionQuestion<ReflectionQuestionType>): void {
    const formArrayQuestions = this.formGroup.controls.questions as FormArray;
    formArrayQuestions.push(this.formBuilder.group(this.toQuestionControlsConfig(question)));
  }

  private toQuestionControlsConfig(question: ReflectionQuestion<ReflectionQuestionType>): IReflectionQuestionControlsConfig {
    return {
      id: [question.id],
      text: [question.text, [Validators.required, Validators.maxLength(200), Validators.minLength(1)]],
      isEnabled: [question.isEnabled],
      isDefault: [question.isDefault],
      type: [question.type],
    };
  }

  public updateQuestion(question: UpdateReflectionQuestionType): void {
    this.reflectionsFacade
      .patchReflectionQuestion$({ reflectionId: this.teamReflection.id, questionId: question.id, payload: question })
      .pipe(take(1))
      .subscribe(() => {
        this.questions = this.questions.map((q) => {
          if (q.id === question.id) {
            q = { ...q, ...question };
          }
          return q;
        });
      });
  }

  public deleteQuestion(params: { questionId: string; index: number }): void {
    this.reflectionsFacade
      .deleteReflectionQuestion$({ reflectionId: this.teamReflection.id, questionId: params.questionId })
      .pipe(take(1))
      .subscribe(() => {
        (this.formGroup.controls.questions as FormArray).removeAt(params.index);
        this.questions = this.questions.filter((q) => q.id !== params.questionId);
      });
  }

  public close(): void {
    this.modalInstance.instance.dismiss();
  }

  public onToggleReflection(): void {
    this.reflectionsFacade
      .patchReflection$({ reflectionId: this.teamReflection.id, payload: { isEnabled: !this.isTeamReflectionEnabled } })
      .pipe(take(1))
      .subscribe(() => {
        this.isTeamReflectionEnabled = !this.isTeamReflectionEnabled;
        this.teamReflection.isEnabled = this.isTeamReflectionEnabled;
      });
  }

  public toggleCustomQuestion(): void {
    this.showQuestionInput = !this.showQuestionInput;
    this.formGroup.controls["newQuestionTitle"].setValue("");
  }

  public addCustomQuestion(): void {
    if (this.formGroup.controls.newQuestionTitle.status === "INVALID") {
      return;
    }

    this.showQuestionInput = false;
    const newQuestion = generateCustomReflectionQuestion({ text: this.formGroup.controls["newQuestionTitle"].value });

    this.reflectionsFacade
      .postReflectionQuestion$({ reflectionId: this.teamReflection.id, payload: newQuestion })
      .pipe(take(1))
      .subscribe((data: ReflectionQuestion<ReflectionQuestionType>) => {
        this.addFormQuestion({ ...newQuestion, id: data.id });
      });

    this.formGroup.controls["newQuestionTitle"].setValue("");
  }
}
